import { useEffect, useState } from 'react';
import { Link, useRouter } from '../router';
import { api } from '../api';
import { UiNode } from '../lib/UiNode';
import { TasksList } from '../components/TasksList';
import { ComingUp } from '../components/ComingUp';
import type { BoardSummary, User } from '../../shared/types';
import { phaseLabel, phaseAccent } from '../lib/phases';

export function BoardsList({ user }: { user: User }) {
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const { navigate } = useRouter();

  useEffect(() => {
    void api.myBoards().then((r) => setBoards(r.boards));
  }, []);

  const visible = boards?.filter((b) => showArchived || b.phase !== 'archived') ?? null;
  const archivedCount = (boards ?? []).filter((b) => b.phase === 'archived').length;

  return (
    <UiNode name="boards-list-screen" entity="boards_list" className="flex flex-col gap-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-medium tracking-tight text-cream sm:text-[24px]">
            {firstName(user.name)}
          </h1>
          <p className="mt-1 text-sm text-cream-3">
            {boards === null
              ? ' '
              : boards.length === 0
                ? 'Things you need to do.'
                : `Things you need to do.`}
          </p>
        </div>
        <UiNode
          as="button"
          name="boards-new-board-button"
          entity="board"
          action="create_start"
          className="btn btn-primary"
          onClick={() => setCreating(true)}
        >
          New board
        </UiNode>
      </div>

      <ComingUp />

      <section className="flex flex-col gap-2">
        <TasksList emptyHint="No tasks across your boards. Things people @-mention you in will show up here." />
      </section>

      <div className="-mb-2 flex items-center justify-between border-t border-ink-rim/60 pt-4">
        <h2 className="text-xs uppercase tracking-wide text-cream-3">Your boards</h2>
        {archivedCount > 0 && (
          <button
            type="button"
            className="text-[11px] text-cream-3 hover:text-cream-2"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
          </button>
        )}
      </div>

      {boards === null ? (
        <BoardsSkeleton />
      ) : visible && visible.length === 0 ? (
        <EmptyState onNew={() => setCreating(true)} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(visible ?? []).map((b) => (
            <UiNode
              as={Link}
              name="boards-board-card"
              entity="board"
              entityId={b.id}
              action="open"
              state={b.phase}
              key={b.id}
              to={`/boards/${b.id}`}
              className="panel panel-interactive group relative overflow-hidden p-6"
            >
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 top-0 h-[2px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, var(--color-clove) 30%, var(--color-clove-2) 70%, transparent)',
                }}
              />
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-[18px] font-medium tracking-tight">{b.name}</h2>
                  {b.tagline && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-cream-3">{b.tagline}</p>
                  )}
                </div>
                <span className={`chip ${phaseAccent(b.phase) ? 'chip-accent' : ''}`}>
                  {phaseLabel(b.phase)}
                </span>
              </div>
              <div className="mt-6 flex items-center justify-between text-xs text-cream-3">
                <span>
                  {b.ideaCount} idea{b.ideaCount === 1 ? '' : 's'} · {b.memberCount} member
                  {b.memberCount === 1 ? '' : 's'}
                </span>
                {b.role === 'owner' && <span>Owner</span>}
              </div>
            </UiNode>
          ))}
        </div>
      )}

      {creating && (
        <NewBoardDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/boards/${id}`)}
        />
      )}
    </UiNode>
  );
}

function firstName(name: string): string {
  return name.split(/\s+/)[0] ?? name;
}

function BoardsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="panel shimmer relative h-[112px] overflow-hidden opacity-60"
          style={{ animationDelay: `${i * 90}ms` }}
        />
      ))}
    </div>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <UiNode
      name="boards-empty-state"
      entity="boards_list"
      state="empty"
      className="panel flex flex-col items-center gap-3 px-6 py-14 text-center"
    >
      <h2 className="text-lg">Start with a board.</h2>
      <p className="max-w-xs text-sm text-cream-3">
        A board holds one project — a hackathon, a retro, an offsite. Capture ideas, sort what
        lands, ship the plan.
      </p>
      <UiNode
        as="button"
        name="boards-new-board-button"
        entity="board"
        action="create_start"
        className="btn btn-accent mt-2"
        onClick={onNew}
      >
        New board
      </UiNode>
    </UiNode>
  );
}

function NewBoardDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [tagline, setTagline] = useState('');
  const [groupType, setGroupType] = useState<
    'team' | 'colleagues' | 'friends' | 'family' | 'mates' | 'other' | ''
  >('');
  // Free-text date hint ("third weekend of June", "5–9 Jun", "next week"…)
  // — the AI planner resolves it to concrete dates at generate time.
  const [when, setWhen] = useState('');
  const [showExactDates, setShowExactDates] = useState(false);
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <UiNode
      name="boards-new-board-dialog"
      entity="board"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <form
        className="panel-elev m-3 w-full max-w-md p-5"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          try {
            const r = await api.createBoard({
              name: name.trim(),
              tagline: tagline.trim() || undefined,
              groupType: groupType || undefined,
              // Prefer exact dates if the user opened the picker and
              // filled them; otherwise hand the free-text hint to the
              // planner.
              startsOn: showExactDates && startsOn ? startsOn : undefined,
              endsOn: showExactDates && endsOn ? endsOn : undefined,
              dateMode:
                showExactDates && startsOn && endsOn
                  ? 'fixed'
                  : when.trim()
                    ? 'fuzzy'
                    : 'unset',
              dateHint: !showExactDates && when.trim() ? when.trim() : undefined,
            });
            onCreated(r.id);
          } finally {
            setBusy(false);
          }
        }}
      >
        <h2 className="text-lg">New board</h2>
        <p className="mt-1 text-xs text-cream-3">What are you planning?</p>

        <div className="mt-4 flex flex-col gap-3">
          <input
            className="input"
            placeholder="Spring hackathon"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="input"
            placeholder="One-line teaser (optional)"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
          />
          <label className="flex flex-col gap-1 text-xs text-cream-3">
            Who's this for?
            <select
              className="input"
              value={groupType}
              onChange={(e) =>
                setGroupType(e.target.value as typeof groupType)
              }
            >
              <option value="">Tell us later</option>
              <option value="team">Team</option>
              <option value="colleagues">Colleagues</option>
              <option value="friends">Friends</option>
              <option value="family">Family</option>
              <option value="mates">Mates</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-cream-3">
            When?
            <input
              className="input"
              placeholder='e.g. "third weekend of June", "5–9 Jun", "next week"'
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
            <span className="text-[11px] text-cream-4">
              The AI works out the dates from this when it drafts the plan.
            </span>
          </label>
          {!showExactDates ? (
            <button
              type="button"
              className="self-start text-[11px] text-cream-3 hover:text-cream-2"
              onClick={() => setShowExactDates(true)}
            >
              I already know the exact dates →
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs text-cream-3">
                Starts
                <input
                  className="input"
                  type="date"
                  value={startsOn}
                  onChange={(e) => setStartsOn(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-cream-3">
                Ends
                <input
                  className="input"
                  type="date"
                  value={endsOn}
                  onChange={(e) => setEndsOn(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <UiNode
            as="button"
            name="boards-new-board-cancel-button"
            entity="board"
            action="create_cancel"
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
          >
            Cancel
          </UiNode>
          <UiNode
            as="button"
            name="boards-new-board-submit-button"
            entity="board"
            action="create_confirm"
            state={busy ? 'busy' : 'idle'}
            type="submit"
            className="btn btn-primary"
            disabled={!name.trim() || busy}
          >
            {busy ? 'Thinking…' : 'Create'}
          </UiNode>
        </div>
      </form>
    </UiNode>
  );
}
